import type { MedplumClient } from '@medplum/core';
import type { CarePlan, Goal, QuestionnaireResponse } from '@medplum/fhirtypes';
import { TREATMENT_PLAN_CAREPLAN_IDENTIFIER_SYSTEM } from '@unify/fhir-model/constants';
import {
  buildTreatmentPlanItemsFromDraft,
  resolveTreatmentPlanLinkIdPrefix,
} from '@unify/fhir-model/resources/treatment-plan';
import { parseTreatmentPlanFromCarePlan } from '@unify/fhir-model/resources/treatment-plan-careplan';
import { isMedplumForbiddenError } from '@/pages/appointments/utils/medplumClientErrors';
import { pickResourceVersionAsOf } from './resourceVersionAsOf';

async function loadCarePlanGoals(
  medplum: MedplumClient,
  carePlan: CarePlan,
  asOfIso?: string,
): Promise<Goal[]> {
  const goalIds = (carePlan.goal ?? [])
    .map((reference) => reference.reference?.replace(/^Goal\//, ''))
    .filter((id): id is string => typeof id === 'string' && !id.startsWith('urn:'));

  const goals = await Promise.all(
    goalIds.map(async (id) => {
      if (!asOfIso) return medplum.readResource('Goal', id);
      try {
        const history = await medplum.readHistory('Goal', id);
        const version = pickResourceVersionAsOf(history, asOfIso);
        if (!version) return undefined;
        if (version.meta?.versionId) {
          return medplum.readVersion('Goal', id, version.meta.versionId);
        }
        return version;
      } catch {
        return medplum.readResource('Goal', id);
      }
    }),
  );
  return goals.flatMap((goal) => (goal ? [goal] : []));
}

/**
 * Replace clinical QuestionnaireResponse.item with content from the CarePlan so
 * the chart-document form never shows a different clinical plan than the CarePlan.
 * When `carePlanVersionId` is set, Goals are resolved as-of that CarePlan version.
 *
 * Portal policies omit CarePlan/Goal, as do staff policies until the AccessPolicy
 * seed has been re-run. Those roles fall back to the QuestionnaireResponse as
 * authored rather than losing access to the whole chart document.
 */
export async function hydrateTreatmentPlanQrFromCarePlan<T extends QuestionnaireResponse>(
  medplum: MedplumClient,
  qr: T,
  options?: { carePlanVersionId?: string },
): Promise<T> {
  if (!qr.id) return qr;

  try {
    const plans = await medplum.searchResources('CarePlan', {
      identifier: `${TREATMENT_PLAN_CAREPLAN_IDENTIFIER_SYSTEM}|${qr.id}`,
      _count: '1',
    });
    const current = plans[0] as CarePlan | undefined;
    if (!current?.id) return qr;

    const carePlan = options?.carePlanVersionId
      ? await medplum.readVersion('CarePlan', current.id, options.carePlanVersionId)
      : current;

    const asOfIso = options?.carePlanVersionId
      ? carePlan.meta?.lastUpdated
      : undefined;
    const goals = await loadCarePlanGoals(medplum, carePlan, asOfIso);
    const draft = parseTreatmentPlanFromCarePlan(carePlan, goals);
    const prefix = resolveTreatmentPlanLinkIdPrefix(qr.item, qr.questionnaire);
    return {
      ...qr,
      item: buildTreatmentPlanItemsFromDraft(draft, prefix),
    };
  } catch (err) {
    if (isMedplumForbiddenError(err) && !options?.carePlanVersionId) return qr;
    throw err;
  }
}
